// 导入获取微信配置的函数
import { fetchWxConfig } from '../apis/api';
// 导入浏览器版本检测和加载脚本的工具函数
import { browerVersion, loadScript } from './utils';

// 导入分享用的logo图片
const shareLogo = require('../assets/images/share-logo.jpg');

// 获取浏览器版本信息
const version = browerVersion();

/**
 * 微信分享数据类型定义
 */
export type WxShareData = {
  title: string; // 分享标题
  desc: string; // 分享描述
  imgUrl: string; // 分享图片地址
  link?: string; // 分享链接，可选
};

// 定义默认的分享数据
const defaultShareData = {
  title: process.env.WECHAT_SHARE_TITLE, // 默认标题
  desc: process.env.WECHAT_SHARE_DESC, // 默认描述
  imgUrl: shareLogo, // 默认分享图片
  link: window.location.href, // 默认分享链接为当前页面地址
};

// 标记微信配置是否已完成
let isFinishConfig = false;

/**
 * 初始化微信配置
 * @returns Promise<boolean> 配置是否成功完成的Promise
 */
const initWxConfig = () => {
  // 加载微信JS-SDK脚本
  const scriptPromise = new Promise((resolve) => {
    loadScript({
      url: 'https://res.wx.qq.com/open/js/jweixin-1.6.0.js',
      name: 'wxJsSdk',
      loadFn: () => {
        resolve(true);
      },
    });
  });
  // 微信配置及SDK加载完成后的处理
  return new Promise((resolve) => {
    Promise.all([fetchWxConfig(), scriptPromise]).then((reslist) => {
      const [fetchRes] = reslist;
      wx.config({
        appId: fetchRes.appid,
        timestamp: fetchRes.timestamp,
        nonceStr: fetchRes.nonceStr,
        signature: fetchRes.signature,
        jsApiList: [
          'updateAppMessageShareData',
          'updateTimelineShareData',
          'onMenuShareAppMessage',
          'onMenuShareTimeline',
        ],
      });
      wx.ready(() => {
        isFinishConfig = true;
        resolve(true);
      });
    });
  });
};

/**
 * 检查JSAPI结果格式是否正确
 * @param value - 结果值，可以是字符串或布尔值
 * @returns boolean - 结果是否为真
 */
const checkJsApiResultFormat = (value: string | boolean) => {
  if (typeof value === 'string' && value === 'true') {
    return true;
  }
  if (typeof value === 'boolean' && value === true) {
    return true;
  }
  return false;
};

/**
 * 初始化微信分享
 * @param shareData - 自定义的分享数据，可选
 */
export const initWxShare = async (shareData?: WxShareData) => {
  // 如果不是在微信浏览器，直接返回
  if (!version.weixin) {
    return;
  }
  // 如果微信配置未完成，先进行配置
  if (!isFinishConfig) {
    await initWxConfig();
  }
  // 合并自定义分享数据和默认分享数据
  const {
    title,
    desc,
    imgUrl,
    link = window.localStorage.href,
  } = { ...defaultShareData, ...shareData };
  // 定义分享到朋友和分享到朋友圈的信息
  const shareAppMessage = {
    title,
    desc,
    link,
    imgUrl,
  };
  const timelineMessage = {
    title,
    link,
    imgUrl,
  };
  // 检查JSAPI支持情况，并根据支持情况设置分享信息
  wx.checkJsApi({
    jsApiList: [
      'updateAppMessageShareData',
      'updateTimelineShareData',
      'onMenuShareAppMessage',
      'onMenuShareTimeline',
    ],
    success: (res: any) => {
      // 如果没有检查结果或不支持相应的API，则直接设置分享信息
      if (!res || !res.checkResult) {
        wx.updateAppMessageShareData(shareAppMessage);
        wx.updateTimelineShareData(timelineMessage);
        return;
      }
      // 根据支持的API设置分享信息
      const {
        updateAppMessageShareData,
        onMenuShareAppMessage,
        updateTimelineShareData,
        onMenuShareTimeline,
      } = res.checkResult;
      if (checkJsApiResultFormat(updateAppMessageShareData)) {
        wx.updateAppMessageShareData(shareAppMessage);
      } else if (checkJsApiResultFormat(onMenuShareAppMessage)) {
        wx.onMenuShareAppMessage(shareAppMessage);
      }
      if (checkJsApiResultFormat(updateTimelineShareData)) {
        wx.updateTimelineShareData(timelineMessage);
      } else if (checkJsApiResultFormat(onMenuShareTimeline)) {
        wx.onMenuShareTimeline(timelineMessage);
      }
    },
    fail: () => {
      // 如果检查失败，则直接设置分享信息
      wx.updateAppMessageShareData(shareAppMessage);
      wx.updateTimelineShareData(timelineMessage);
    },
  });
};
